import { Field, Area, Section } from "./fields";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export const AREAS = [
  { key: "exterior", title: "Home Exterior & Entry", items: ["Steps", "Handrails", "Lighting", "Thresholds"] },
  { key: "interior", title: "General Interior", items: ["Cleanliness", "Trip Hazards", "Clutter", "Lighting", "Smoke Detectors"] },
  { key: "bathroom", title: "Bathroom (High Risk)", items: ["Tub Type", "Grab Bars", "Floor Surfaces", "Toilet Height"] },
  { key: "bedroom", title: "Bedroom", items: ["Bed Height", "Path to Bathroom", "Nightlights", "Medical Equipment"] },
  { key: "kitchen", title: "Kitchen", items: ["Stove Safety", "Accessibility", "Expired Food", "Fire Hazards"] },
  { key: "storage", title: "Medications & Equipment Storage", items: ["Medication Storage", "Equipment Storage"] },
  { key: "clinical", title: "General Observations (what you notice — not a clinical assessment)", items: ["Moving Around the Home", "Memory & Conversation", "Mood & Outlook", "Personal Care Routines", "Daily Activity Concerns"] },
];

export const emptyEnvironmental = {
  assessor: "", assessment_date: "", overall_risk: "Low", equipment_recommendations: "", summary_notes: "",
  areas: Object.fromEntries(AREAS.map((a) => [a.key, a.items.map((name) => ({ name, status: "OK", note: "" }))])),
};

const STATUS_STYLES = {
  OK: "bg-[#27AE60]/15 text-[#1e8449] border-[#27AE60]/40",
  Concern: "bg-[#D68910]/15 text-[#9a6a0b] border-[#D68910]/40",
  Hazard: "bg-[#C0392B]/15 text-[#C0392B] border-[#C0392B]/40",
  "N/A": "bg-stone-100 text-stone-500 border-stone-300",
};

export default function EnvironmentalForm({ data, onChange }) {
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setItem = (areaKey, i, patch) => {
    const areas = { ...data.areas, [areaKey]: data.areas[areaKey].map((it, idx) => (idx === i ? { ...it, ...patch } : it)) };
    onChange({ ...data, areas });
  };

  return (
    <div className="space-y-6" data-testid="environmental-form">
      <div className="rounded-lg border border-stone-200 bg-accent p-4 text-sm text-stone-600" data-testid="env-path-notice">
        Follow the path of travel: bedroom → bathroom → kitchen → exit. Mark each item OK, Concern, Hazard, or N/A.
      </div>
      <Section title="Visit Details">
        <Field label="Assessor name" tid="env-assessor" value={data.assessor} onChange={set("assessor")} />
        <Field label="Visit date" tid="env-date" type="date" value={data.assessment_date} onChange={set("assessment_date")} />
      </Section>

      {AREAS.map((area) => (
        <div key={area.key} className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid={`env-area-${area.key}`}>
          <h3 className="font-heading text-lg font-semibold text-stone-900">{area.title}</h3>
          <div className="mt-4 space-y-3">
            {data.areas[area.key].map((item, i) => (
              <div key={item.name} className="flex flex-wrap items-center gap-3">
                <span className="w-40 text-sm font-medium text-stone-700">{item.name}</span>
                <div className="flex gap-1.5">
                  {Object.keys(STATUS_STYLES).map((s) => (
                    <button key={s} type="button" onClick={() => setItem(area.key, i, { status: s })}
                      data-testid={`env-${area.key}-${i}-${s.toLowerCase().replace("/", "-")}`}
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-all ${
                        item.status === s ? STATUS_STYLES[s] + " ring-1 ring-offset-1 ring-stone-400" : "border-stone-200 bg-white text-stone-400 hover:border-stone-400 hover:text-stone-700"
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>
                <Input placeholder="Note" value={item.note} onChange={(e) => setItem(area.key, i, { note: e.target.value })}
                  data-testid={`env-${area.key}-${i}-note`} className="h-8 min-w-[200px] flex-1 border-stone-300 bg-white text-sm" />
              </div>
            ))}
          </div>
        </div>
      ))}

      <Section title="Overall Risk & Recommendations">
        <div className="space-y-1.5">
          <Label>Overall home safety risk level</Label>
          <div className="flex gap-2" data-testid="env-overall-risk">
            {["Low", "Moderate", "High"].map((r) => (
              <button key={r} type="button" onClick={() => onChange({ ...data, overall_risk: r })}
                data-testid={`env-risk-${r.toLowerCase()}`}
                className={`rounded-md border px-4 py-2 text-sm font-medium transition-all ${
                  data.overall_risk === r
                    ? (r === "High" ? STATUS_STYLES.Hazard : r === "Moderate" ? STATUS_STYLES.Concern : STATUS_STYLES.OK) + " ring-1 ring-offset-1 ring-stone-400"
                    : "border-stone-200 bg-white text-stone-400 hover:border-stone-400 hover:text-stone-700"
                }`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2"><Area label="Equipment recommendations (grab bars, shower bench, raised toilet seat...)" tid="env-equipment" value={data.equipment_recommendations} onChange={set("equipment_recommendations")} /></div>
        <div className="md:col-span-2"><Area label="Summary notes" tid="env-summary" value={data.summary_notes} onChange={set("summary_notes")} /></div>
      </Section>
    </div>
  );
}
